import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { getTimetable, ITimetableResponseBody } from 'src/api/getTimetable';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { groupSchedulesByDay, ITimetableDay } from 'src/utils/timetables/groupSchedulesByDay';
import { groupSchedulesByEntity, ITimetableEntityGroup } from 'src/utils/timetables/groupSchedulesByEntity';
import { useUserTracking } from '../useUserTracking/useUserTracking';
import { QueryKeys } from '../QueryKeys';

/**
 * Hook for fetching and shaping a single timetable.
 * @returns {UseQueryResult} - Query result with:
 *   - query.data.data: raw timetable detail (schedules flat, as returned by the API)
 *   - byDay: schedules grouped into 7 days (Mon-Sun), each split into menu/price-scheme lanes,
 *     sorted by start time
 *   - byEntity: schedules grouped by unique menu/price-scheme, each with the set of days it
 *     appears on
 */
export const useTimetable = (
  timetableId?: string,
  options?: UseQueryOptions<ITimetableResponseBody>,
): UseQueryResult<ITimetableResponseBody> & {
  byDay: ITimetableDay[];
  byEntity: ITimetableEntityGroup[];
} => {
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();

  const query = useQuery<ITimetableResponseBody>({
    queryKey: [QueryKeys.timetable, getBrandId(), timetableId],
    queryFn: async () => getTimetable(timetableId),
    enabled: !!timetableId,
    onError: (error) => {
      console.error('Error fetching timetable:', error);
      captureException(error, { tags: { action: 'fetch_timetable', timetableId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst fetching the timetable' });
    },
    ...options,
  });

  const byDay = useMemo(() => (query.data ? groupSchedulesByDay(query.data.data) : []), [query.data]);
  const byEntity = useMemo(() => (query.data ? groupSchedulesByEntity(query.data.data) : []), [query.data]);

  return { ...query, byDay, byEntity };
};
